import { useChatMessages, useChatStatus } from '@ai-sdk-tools/store';
import { useMutation } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import type { ToolUIPart } from 'ai';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { NovaScheduleProposalCard } from './nova-schedule-proposal-card';
import { renderToolPendingState } from './tool-pending-state';
import {
  isNovaScheduleProposalToolPart,
  isResolvedNovaScheduleProposalOutput,
  NOVA_SCHEDULE_PROPOSAL_TOOL_NAME,
  NOVA_SCHEDULES_FEATURE_FLAG,
  type NovaScheduleProposalDraft,
  type NovaScheduleProposalResolution,
  type NovaScheduleProposalToolPart,
} from '../../../../lib/nova-schedule-proposal';
import type { UIMessageWithMetadata } from '../../../../lib/types';
import { CREATE_NOVA_SCHEDULE, type CreateNovaScheduleCommand } from '../../../../queries/nova-schedule-mutations';
import type { SelectedAccount } from '../../../account-selector';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { useIsSchedulePreviewHydrating } from '../../contexts/durable-preview-context';
import { useToolHandoffContinuation } from '../../hooks/use-tool-handoff-continuation';
import { normalizeChatUuid } from '../../lib/notification-helpers';
import { getMessageAttachmentFiles } from '../../lib/tool-generated-files';

interface NovaScheduleProposalToolProps {
  part: ToolUIPart;
  messageUuid: string;
}

type PendingAction = 'confirm' | 'cancel';
const CREATE_ERROR_MESSAGE = "We couldn't create this schedule. Check the details and try again.";
const CONTINUATION_ERROR_MESSAGE =
  "The schedule action completed, but Nova couldn't resume. Try the same action again to continue.";

export function NovaScheduleProposalTool({ part }: NovaScheduleProposalToolProps) {
  const schedulesEnabled = useFeatureFlag(NOVA_SCHEDULES_FEATURE_FLAG, false);

  if (!schedulesEnabled || !isNovaScheduleProposalToolPart(part)) {
    return null;
  }

  return <EnabledNovaScheduleProposalTool part={part} />;
}

function EnabledNovaScheduleProposalTool({ part }: { part: NovaScheduleProposalToolPart }) {
  const {
    canSelectAccount,
    chatUuid,
    isPublicMode,
    isReadOnly,
    selectedAccount,
    setSettingsSection,
    userScopes,
    userUuid,
  } = useChatOverlayContext();
  const [createNovaSchedule] = useMutation(CREATE_NOVA_SCHEDULE);
  const messages = useChatMessages<UIMessageWithMetadata>();
  const status = useChatStatus();
  const isSchedulePreviewHydrating = useIsSchedulePreviewHydrating();
  const { submitToolHandoffContinuation } = useToolHandoffContinuation();
  const [scheduleUuid] = useState(() => uuidv4());
  const [pendingAction, setPendingAction] = useState<PendingAction>();
  const [error, setError] = useState<string>();

  const handleManageSchedules = useCallback(() => {
    setSettingsSection('scheduled');
  }, [setSettingsSection]);

  const submitResolution = useCallback(
    async (action: PendingAction, output: NovaScheduleProposalResolution) => {
      setPendingAction(action);
      setError(undefined);

      try {
        const continued = await submitToolHandoffContinuation({
          tool: NOVA_SCHEDULE_PROPOSAL_TOOL_NAME,
          toolCallId: part.toolCallId,
          output,
        });

        if (!continued) {
          setError(CONTINUATION_ERROR_MESSAGE);
        }
      } catch {
        setError(CONTINUATION_ERROR_MESSAGE);
      } finally {
        setPendingAction(undefined);
      }
    },
    [part.toolCallId, submitToolHandoffContinuation],
  );

  const handleConfirm = useCallback(
    async (draft: NovaScheduleProposalDraft, account: SelectedAccount) => {
      setPendingAction('confirm');
      setError(undefined);

      const scheduleAccount = canSelectAccount ? account : undefined;

      const command: CreateNovaScheduleCommand = {
        uuid: scheduleUuid,
        ownerUserUuid: userUuid,
        sourceChatUuid: normalizeChatUuid(chatUuid),
        ...(scheduleAccount?.uuid ? { accountUuid: scheduleAccount.uuid } : {}),
        title: draft.title,
        prompt: draft.prompt,
        ...(draft.attachmentFileUuids?.length ? { attachmentFileUuids: draft.attachmentFileUuids } : {}),
        timezone: draft.timezone,
        recurrence: draft.recurrence,
        ...(draft.startAt ? { startAt: draft.startAt } : {}),
        ...(draft.outputPolicy ? { outputPolicy: draft.outputPolicy } : {}),
      };

      try {
        const result = await createNovaSchedule({ variables: { command } });
        if (result.data?.createNovaSchedule.uuid !== scheduleUuid) {
          throw new Error('The create mutation did not return the requested schedule.');
        }
      } catch (createError) {
        setError(getCreateErrorMessage(createError));
        setPendingAction(undefined);
        return;
      }

      await submitResolution('confirm', { action: 'confirm', scheduleUuid });
    },
    [canSelectAccount, chatUuid, createNovaSchedule, scheduleUuid, submitResolution, userUuid],
  );

  const handleCancel = useCallback(async () => {
    await submitResolution('cancel', { action: 'cancel' });
  }, [submitResolution]);

  if (part.state === 'input-streaming') {
    return renderToolPendingState(NOVA_SCHEDULE_PROPOSAL_TOOL_NAME);
  }

  if (part.state === 'output-available' && isResolvedNovaScheduleProposalOutput(part.output)) {
    return (
      <NovaScheduleProposalCard
        state="resolved"
        proposal={part.input}
        output={part.output}
        onManageSchedules={handleManageSchedules}
      />
    );
  }

  if (part.state === 'output-error') {
    return part.input ? (
      <NovaScheduleProposalCard
        state="pending"
        proposal={part.input}
        error={part.errorText}
        disabled
        showAccount={canSelectAccount}
        initialAccount={canSelectAccount ? selectedAccount : undefined}
        initialAttachmentFiles={getScheduleAttachmentFiles(part.input.attachmentFileUuids, messages)}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    ) : null;
  }

  if (part.state !== 'input-available' && part.state !== 'output-available') {
    return null;
  }

  return (
    <NovaScheduleProposalCard
      state="pending"
      proposal={part.input}
      previewPending={
        !part.input.preview?.firstRunAt &&
        (status === 'submitted' || status === 'streaming' || isSchedulePreviewHydrating)
      }
      initialAccount={canSelectAccount ? selectedAccount : undefined}
      initialAttachmentFiles={getScheduleAttachmentFiles(part.input.attachmentFileUuids, messages)}
      showAccount={canSelectAccount}
      canSelectAccount={canSelectAccount}
      userUuid={userUuid}
      userScopes={userScopes}
      pendingAction={pendingAction}
      error={error}
      disabled={isReadOnly || isPublicMode === true || part.state !== 'input-available'}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    />
  );
}

function getScheduleAttachmentFiles(fileUuids: string[] | undefined, messages: UIMessageWithMetadata[]) {
  if (!fileUuids?.length) return [];

  const filesByUuid = new Map(
    messages
      .flatMap(getMessageAttachmentFiles)
      .map((file) => [file.uuid, { uuid: file.uuid, filename: file.filename }]),
  );
  return fileUuids.flatMap((uuid) => {
    const file = filesByUuid.get(uuid);
    return file ? [file] : [];
  });
}

function getCreateErrorMessage(error: unknown): string {
  if (!(error instanceof Error) || !error.message.trim()) {
    return CREATE_ERROR_MESSAGE;
  }

  return `${CREATE_ERROR_MESSAGE} ${error.message}`;
}
